import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function XRangeSection() {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: 'ri-vip-crown-line',
      title: 'Premium Construction',
      description: 'Galvanized steel frame with aluminium alloy shell and polyurethane insulation for superior quality'
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Versatile Applications',
      description: 'Perfect for residential living, commercial spaces, hospitality, and temporary accommodations'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Exceptional Durability',
      description: 'Weather-resistant materials and robust engineering ensure long-lasting performance'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Quick Installation',
      description: 'Prefabricated design allows for rapid deployment with minimal site preparation'
    }
  ];

  const models = [
    {
      name: 'X3',
      dimensions: '5.6m × 3.3m × 3.2m',
      area: '18 m²',
      occupancy: '2 persons',
      image: '/images/71de8de379a3.webp',
      description: 'Compact and agile, perfect for high-end glamping or private studios.'
    },
    {
      name: 'X5',
      dimensions: '8.5m × 3.3m × 3.2m',
      area: '28 m²',
      occupancy: '2-4 persons',
      image: '/images/65c0b0130fa8.webp',
      description: 'Features a panoramic balcony and bedroom skylight for an immersive nature experience.'
    },
    {
      name: 'X6',
      dimensions: '9.5m × 3.3m × 3.2m',
      area: '31 m²',
      occupancy: '2-4 persons',
      image: '/images/8ccdb45466f9.webp',
      description: 'Extended living space with a dedicated equipment room for maximum utility.'
    },
    {
      name: 'X7',
      dimensions: '11.5m × 3.3m × 3.2m',
      area: '38 m²',
      occupancy: '2-4 persons',
      image: '/images/8d92522722bb.webp',
      description: 'The flagship model offering maximum luxury, large panoramic views, and a spacious layout.'
    },
    {
      name: 'Y6',
      dimensions: '7.5m × 3.3m × 3.2m',
      area: '24.7 m²',
      occupancy: '2 persons',
      image: '/images/nx-y6-white-background-46281b.webp',
      description: 'Cabin-style living capsule with wooden exterior and panoramic balcony, offering a warm and immersive nature experience.'
    },
    {
      name: 'V6',
      dimensions: '5.55m × 4.05m × 2.55m / 5.55m × 2.92m × 2.55m',
      area: '22.4 m² + 16 m²',
      occupancy: '2-4 persons',
      image: '/images/nx-p03-img01-4472ca.webp',
      description: 'Innovative triangle-shaped double-layer design with a glass facade, maximising vertical space efficiency and panoramic views.'
    }
  ];

  return (
    <section id="x-range" className="relative py-16 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-3 px-4 sm:px-6 py-2 sm:py-3 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-4 sm:mb-6">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-cyan-500 rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(0,217,255,0.5)]">
              <span className="text-base sm:text-lg font-bold text-black">X</span>
            </div>
            <span className="text-cyan-400 text-xs sm:text-sm uppercase tracking-widest">X SERIES</span>
          </div>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-thin mb-4 sm:mb-6 px-4 text-white">
            Premium <span className="text-cyan-400" style={{ textShadow: '0 0 30px rgba(0,217,255,0.6)' }}>Space Capsules</span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 max-w-3xl mx-auto px-4">
            High-end prefabricated mobile homes combining futuristic design with exceptional quality
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center mb-12 sm:mb-16 lg:mb-20 w-full">
          {/* Left: Features */}
          <div className="space-y-4 sm:space-y-6 order-2 lg:order-1 w-full">
            {features.map((feature, index) => (
              <div
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                onClick={() => setActiveFeature(index)}
                className={`p-6 sm:p-8 rounded-2xl border transition-all duration-300 cursor-pointer w-full ${
                  activeFeature === index
                    ? 'bg-cyan-500/10 border-cyan-400/50 shadow-[0_0_30px_rgba(0,217,255,0.2)]'
                    : 'bg-black/40 border-cyan-500/20 hover:border-cyan-500/40'
                }`}
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${
                    activeFeature === index ? 'bg-cyan-500 shadow-[0_0_20px_rgba(0,217,255,0.5)]' : 'bg-cyan-500/20'
                  }`}>
                    <i className={`${feature.icon} text-xl sm:text-2xl ${activeFeature === index ? 'text-black' : 'text-cyan-400'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg sm:text-xl font-medium mb-2 text-white">{feature.title}</h3>
                    <p className="text-sm sm:text-base text-gray-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Image */}
          <div className="relative order-1 lg:order-2 w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-cyan-400/20 blur-3xl rounded-full"></div>
            <div className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px]">
              <img 
                src="/images/dabe9d51796d.webp" 
                alt="X Range Space Capsule"
                className="w-full h-full object-cover rounded-3xl shadow-[0_0_40px_rgba(0,217,255,0.3)]"
              />
            </div>
          </div>
        </div>

        {/* Models Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12 lg:mb-16 w-full">
          {models.map((model, index) => (
            <div key={index} className="group relative bg-black/40 backdrop-blur-xl border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-400/50 hover:shadow-[0_0_30px_rgba(0,217,255,0.2)] transition-all duration-500 cursor-pointer w-full">
              <div className="relative w-full h-40 sm:h-48">
                <img 
                  src={model.image}
                  alt={model.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-cyan-500 text-black text-sm font-medium rounded-full shadow-[0_0_15px_rgba(0,217,255,0.5)]">
                  {model.name}
                </div>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Dimensions</span>
                    <span className="font-medium text-white">{model.dimensions}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Area</span>
                    <span className="font-medium text-white">{model.area}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Occupancy</span>
                    <span className="font-medium text-white">{model.occupancy}</span>
                  </div>
                </div>
                <Link to={`/contact?model=${encodeURIComponent(model.name)}`} className="block w-full px-4 py-2 bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-sm font-medium rounded-full hover:bg-cyan-500 hover:text-black transition-all whitespace-nowrap cursor-pointer text-center">
                  Request Quote
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="text-center w-full flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/x-range-details"
            className="group inline-flex items-center gap-3 px-8 py-4 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer"
          >
            Explore X Series Details
            <div className="w-8 h-8 bg-black rounded-full flex items-center justify-center group-hover:shadow-lg transition-all">
              <i className="ri-arrow-right-line text-cyan-400"></i>
            </div>
          </Link>
          <Link to="/contact?model=X%20Series" className="group inline-flex items-center gap-3 px-8 py-4 bg-cyan-600 text-white text-base font-medium rounded-full hover:bg-cyan-500 transition-all hover:shadow-[0_0_30px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
            Request Quote for X Series
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center group-hover:shadow-lg transition-all">
              <i className="ri-mail-send-line text-cyan-600"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
